/** The wallet summary box: current balance, how much expires soon, and the rate for pricing. */
export interface CreditSummary {
  /**
   * What a NEW campaign may actually be funded with: the credits that outlive the campaign, less
   * the headroom the group's live campaigns already hold. Both subtractions matter — credits
   * promised elsewhere would fund two campaigns from one balance, and a lot expiring mid-campaign
   * cannot pay for a redemption after it is gone.
   */
  available: string;
  availableDetail: { balance: string; budgeted: string };
  /** Decimal strings (numeric on the server). */
  balance: string;
  expiringSoon: string;
  /** Credits per 1 USD; null if the ratio isn't configured (then buying is disabled). */
  creditsPerUsd: number | null;
  /** How many months new credits are valid for (from system_parameters); null if unset. */
  expiryMonths: number | null;
}

/** credit_lots.state values (mirrors the backend CreditLotState). */
export const CREDIT_LOT_STATE = {
  provisional: "provisional",
  confirmed: "confirmed",
  hold: "hold",
  reversed: "reversed",
} as const;

/** A credit lot as the API returns it — the Credits Pool contents. */
export interface CreditLot {
  id: number;
  organizationId: number | null;
  sourceType: number;
  sourceId: number | null;
  purchaseDate: string;
  expirationDate: string;
  originalCreditAmount: string;
  remainingCreditAmount: string;
  price: string | null;
  pricingCcy: string | null;
  state: string; // CREDIT_LOT_STATE
  isActive: boolean;
  provisionalAt: string | null;
  confirmedAt: string | null;
  holdAt: string | null;
  holdReason: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
}

/** One row of a lot's activity history (system_event_logs, log_source = "credit_lot"). */
export interface CreditLotEvent {
  id: number;
  eventType: string;
  actorType: number;
  actorUserId: number | null;
  actorName: string | null;
  statusDescription: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

/** What POST /credits/purchases returns — poll the purchase by id from here. */
export interface BuyCreditsResult {
  purchaseId: number;
  status: string; // the Stripe PaymentIntent status
}

/** A purchase's status while the UI polls after Buy. status: 0 new / 1 success / 2 cancelled / 3 failed. */
export interface CreditPurchase {
  id: number;
  status: number;
  credits: string;
  netPrice: string | null;
  createdAt: string;
}

export const PURCHASE_STATUS = { new: 0, success: 1, cancelled: 2, failed: 3 } as const;

/** A group a transfer may target — a sibling in the same organisation. */
export interface TransferTarget {
  id: number;
  name: string;
}

/**
 * What a group may send, and the two numbers behind it.
 *
 * `transferable` is NOT the balance: credits already promised to live campaigns cannot leave, or the
 * campaign keeps a ceiling over stock that has walked out of the group.
 */
export interface SendableLot {
  lotId: number;
  /** What the lot holds. */
  remaining: string;
  /**
   * How much of THIS lot no campaign needs — the cap on the row.
   *
   * Per lot, not one group total, because a transfer moves specific lots: a client under the total
   * cap can still empty the one long-dated lot a long campaign was depending on, since the short
   * lots that cover the shorter campaigns cannot serve it.
   */
  free: string;
  expirationDate: string;
}

export interface TransferableBalance {
  balance: string;
  budgetedToCampaigns: string;
  transferable: string;
  /**
   * The maturities behind the balance, soonest to expire first — one row of the allocation table.
   * Only lots with something spare: a lot entirely spoken for is not a choice the client can make.
   */
  lots: SendableLot[];
  targets: TransferTarget[];
}

/** How much to send from one specific lot. The grain is the maturity, not the total. */
export interface TransferAllocation {
  lotId: number;
  credits: string;
}

export interface CreditTransferResult {
  transferId: number;
  sourceGroupId: number;
  destinationGroupId: number;
  credits: string;
  transferredAt: string;
  journalEntryId: number | null;
  lines: {
    lineId: number;
    sourceLotId: number;
    destinationLotId: number;
    credits: string;
    expirationDate: string;
  }[];
}

/**
 * One row of a group's transfer history.
 *
 * `direction` and `counterpartyName` are relative to the group being viewed: the same transfer is
 * "out" to one end and "in" to the other, and each end cares who the OTHER one was.
 */
export interface CreditTransferListItem {
  id: number;
  direction: "in" | "out";
  counterpartyName: string;
  sourceGroupId: number;
  sourceGroupName: string;
  destinationGroupId: number;
  destinationGroupName: string;
  credits: string;
  note: string | null;
  transferredAt: string;
  reversedAt: string | null;
  actorName: string | null;
}

export interface PaginatedTransfers {
  data: CreditTransferListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
